import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box as MuiBox,
  CircularProgress,
  IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { MessageTemplate, MessageChannel } from '../types/Message';

interface TemplateFormProps {
  template?: MessageTemplate | null;
  onSave: (data: Partial<MessageTemplate>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const CHANNEL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram Direct' },
  { value: 'email', label: 'E-mail' },
  { value: 'sms', label: 'SMS' },
  { value: 'messenger', label: 'Facebook Messenger' },
];

const AVAILABLE_VARIABLES = [
  { label: 'Nome', value: '{name}' },
  { label: 'Empresa', value: '{company}' },
  { label: 'Telefone', value: '{phone}' },
  { label: 'Email', value: '{email}' },
  { label: 'Data', value: '{date}' },
  { label: 'Hora', value: '{time}' },
];

const TemplateForm: React.FC<TemplateFormProps> = ({
  template,
  onSave,
  onCancel,
  loading = false
}) => {
  const [formData, setFormData] = useState<Partial<MessageTemplate>>({
    name: '',
    channel: 'whatsapp',
    subject: '',
    body: '',
    variables: [],
    category: '',
    tags: [],
    is_active: true,
    is_public: true
  });

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (template) {
      setFormData(template);
    }
  }, [template]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: event.target.value
    }));

    // Limpar erro do campo
    if (errors[field]) {
      setErrors((prev: any) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddVariable = (variable: string) => {
    const newBody = (formData.body || '') + ' ' + variable;
    setFormData((prev: any) => ({ ...prev, body: newBody.trim() }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData((prev: any) => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev: any) => ({
      ...prev,
      tags: (prev.tags || []).filter((tag: string) => tag !== tagToRemove)
    }));
  };

  const extractVariables = (text: string): string[] => {
    const regex = /\{([^}]+)\}/g;
    const matches = text.match(regex) || [];
    return [...new Set(matches)];
  };

  const handleBlur = () => {
    // Extrair variáveis do corpo da mensagem
    const extractedVariables = extractVariables(formData.body || '');
    setFormData((prev: any) => ({
      ...prev,
      variables: extractedVariables
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.body?.trim()) {
      newErrors.body = 'Corpo da mensagem é obrigatório';
    }

    if (formData.channel === 'email' && !formData.subject?.trim()) {
      newErrors.subject = 'Assunto é obrigatório para e-mail';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validate()) return;

    try {
      await onSave(formData);
    } catch (err: any) {
      setErrors({ submit: err.message || 'Erro ao salvar template' });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {template ? 'Editar Template' : 'Novo Template'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Informações básicas */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nome *"
              value={formData.name || ''}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Canal *</InputLabel>
              <Select
                value={formData.channel}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, channel: e.target.value }))}
                label="Canal *"
                disabled={loading}
              >
                {CHANNEL_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Assunto (apenas para email) */}
          {formData.channel === 'email' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Assunto *"
                value={formData.subject || ''}
                onChange={handleChange('subject')}
                error={!!errors.subject}
                helperText={errors.subject}
                disabled={loading}
              />
            </Grid>
          )}

          {/* Corpo da mensagem */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Corpo da Mensagem *
            </Typography>
            
            {/* Variáveis disponíveis */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Variáveis disponíveis (clique para inserir):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {AVAILABLE_VARIABLES.map(variable => (
                  <Chip
                    key={variable.value}
                    label={variable.label}
                    size="small"
                    clickable
                    onClick={() => handleAddVariable(variable.value)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <TextField
              fullWidth
              multiline
              rows={6}
              value={formData.body || ''}
              onChange={handleChange('body')}
              onBlur={handleBlur}
              error={!!errors.body}
              helperText={errors.body || 'Use {nome} para inserir variáveis'}
              placeholder="Digite sua mensagem aqui..."
              disabled={loading}
            />

            {/* Variáveis detectadas */}
            {formData.variables && formData.variables.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Variáveis detectadas:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {formData.variables.map((variable, index) => (
                    <Chip
                      key={index}
                      label={variable}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Grid>

          {/* Categoria */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Categoria"
              value={formData.category || ''}
              onChange={handleChange('category')}
              placeholder="Ex: Follow-up, Proposta, Bem-vindo"
              disabled={loading}
            />
          </Grid>

          {/* Tags */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Adicionar tag"
                disabled={loading}
              />
              <IconButton
                size="small"
                onClick={handleAddTag}
                disabled={loading || !newTag.trim()}
                color="primary"
              >
                <AddIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {formData.tags?.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  onDelete={() => handleRemoveTag(tag)}
                  disabled={loading}
                />
              ))}
            </Box>
          </Grid>

          {/* Opções */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">
              Template ativo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formData.is_active ? 'Sim' : 'Não'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">
              Público
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formData.is_public ? 'Sim - Visível para todos' : 'Não - Apenas para mim'}
            </Typography>
          </Grid>

          {/* Erro de submissão */}
          {errors.submit && (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">
                {errors.submit}
              </Typography>
            </Grid>
          )}

          {/* Botões */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                onClick={onCancel}
                disabled={loading}
                variant="outlined"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : undefined}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default TemplateForm;
